import * as React from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { Button } from "./button"
import { cn } from "../../lib/utils"

// Build a compact page list with ellipses, e.g. [1, '...', 4, 5, 6, '...', 20]
function getPageItems(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
  const items = [1]
  const left = Math.max(2, current - 1)
  const right = Math.min(total - 1, current + 1)
  if (left > 2) items.push("…")
  for (let p = left; p <= right; p++) items.push(p)
  if (right < total - 1) items.push("…")
  items.push(total)
  return items
}

/**
 * Pagination footer with range summary and page controls.
 * page is 1-indexed.
 */
function Pagination({ page, pageCount, total, pageSize, onPageChange, className }) {
  if (pageCount <= 1 && total <= pageSize) {
    return (
      <div className={cn("flex items-center px-2 py-3 text-sm text-muted-foreground", className)}>
        {total} {total === 1 ? "item" : "items"}
      </div>
    )
  }

  const start = total === 0 ? 0 : (page - 1) * pageSize + 1
  const end = Math.min(page * pageSize, total)
  const items = getPageItems(page, pageCount)

  return (
    <div
      className={cn(
        "flex flex-col gap-3 px-2 py-3 sm:flex-row sm:items-center sm:justify-between",
        className
      )}
    >
      <p className="text-sm text-muted-foreground">
        Showing <span className="font-medium text-foreground">{start}</span>–
        <span className="font-medium text-foreground">{end}</span> of{" "}
        <span className="font-medium text-foreground">{total}</span>
      </p>

      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          className="h-8 px-2"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          aria-label="Previous page"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        {items.map((it, i) =>
          it === "…" ? (
            <span
              key={`ellipsis-${i}`}
              className="px-2 text-sm text-muted-foreground select-none"
            >
              …
            </span>
          ) : (
            <Button
              key={it}
              variant={it === page ? "default" : "outline"}
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => onPageChange(it)}
            >
              {it}
            </Button>
          )
        )}

        <Button
          variant="outline"
          size="sm"
          className="h-8 px-2"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= pageCount}
          aria-label="Next page"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}

/**
 * Client-side pagination over an array.
 * Returns the current page slice plus everything needed to render <Pagination/>.
 * Resets to page 1 whenever the item count shrinks below the current window.
 */
function usePagination(items, pageSize = 10) {
  const [page, setPage] = React.useState(1)
  const total = items.length
  const pageCount = Math.max(1, Math.ceil(total / pageSize))

  React.useEffect(() => {
    if (page > pageCount) setPage(1)
  }, [page, pageCount])

  const pageItems = React.useMemo(
    () => items.slice((page - 1) * pageSize, page * pageSize),
    [items, page, pageSize]
  )

  return { page, setPage, pageCount, total, pageSize, pageItems }
}

export { Pagination, usePagination }
